import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';

function TermsPage({ Header, Footer }) {
  const handleEmailClick = (e) => {
    e.preventDefault();
    window.location.href = 'mailto:service@8004agents.ai';
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Helmet>
        <title>Terms of Service | ERC-8004 Explorer</title>
        <meta name="description" content="Terms of Service for ERC-8004 Explorer. Please read these terms carefully before using our service." />
        <link rel="canonical" href="https://8004agents.ai/terms" />
      </Helmet>

      <Header
        stats={null}
        chainFilter="all"
        onChainFilterChange={() => {}}
        chains={[]}
        refreshing={false}
        onRefresh={() => {}}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Explorer
        </Link>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 p-6 sm:p-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: January 2025</p>

          <div className="prose prose-gray max-w-none space-y-6">
            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using ERC-8004 Explorer (8004agents.ai), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Description of Service</h2>
              <p className="text-gray-600 leading-relaxed">
                ERC-8004 Explorer is an informational platform that indexes and displays publicly available blockchain data about ERC-8004 AI agents across multiple networks. We provide tools to explore, search, and analyze this data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">3. No Financial Advice</h2>
              <p className="text-gray-600 leading-relaxed">
                Nothing on this website constitutes financial, investment, legal, or other professional advice. The information displayed about AI agents, including reputation scores and validation status, is provided for informational purposes only. Always do your own research before interacting with any blockchain agent or smart contract.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Accuracy of Information</h2>
              <p className="text-gray-600 leading-relaxed">
                We strive to display accurate blockchain data, but we make no guarantees about the accuracy, completeness, or timeliness of information on this site. Blockchain data is indexed from public sources and may have delays or discrepancies.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">5. User Conduct</h2>
              <p className="text-gray-600 leading-relaxed mb-3">You agree not to:</p>
              <ul className="list-disc pl-5 text-gray-600 space-y-2">
                <li>Use our service for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the service</li>
                <li>Scrape or collect data in a way that burdens our infrastructure</li>
                <li>Impersonate others or provide false information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Intellectual Property</h2>
              <p className="text-gray-600 leading-relaxed">
                All original content on the ERC-8004 Explorer website is owned by us or used under applicable licenses.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Third-Party Links</h2>
              <p className="text-gray-600 leading-relaxed">
                Our service may contain links to third-party websites, blockchain explorers, and external resources. We are not responsible for the content or practices of these external sites.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Disclaimer of Warranties</h2>
              <p className="text-gray-600 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO LOSSES FROM INTERACTING WITH BLOCKCHAIN AGENTS OR SMART CONTRACTS.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">10. Indemnification</h2>
              <p className="text-gray-600 leading-relaxed">
                You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the service or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">11. Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">12. Governing Law</h2>
              <p className="text-gray-600 leading-relaxed">
                These terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-gray-900 mb-3">13. Contact</h2>
              <p className="text-gray-600 leading-relaxed">
                For questions about these Terms of Service, contact us at{' '}
                <button
                  onClick={handleEmailClick}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  service@8004agents.ai
                </button>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer stats={null} agents={[]} />
    </div>
  );
}

export default TermsPage;